import { SortingState } from "@tanstack/react-table";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import StatusChip from "components/Chips/StatusChip";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import usePermission from "CustomHooks/usePermission";
import { fetchGRNList } from "slices/Warehouse/ItemInward2/GRN/thunk";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import ActionButton from "components/Buttons/ActionButton";
import { fetchDispatchIssueToLabourList } from "slices/Inventory/Dispatch/IsuueToLabour/thunk";

type DispatchProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const Dispatch: React.FC<DispatchProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Issue To Labour";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  // Redux selectors
  const selectDispatchIssueToLabour = createSelector(
    (state: any) => state.DispatchIssueToLabourReducer,
    (item) => ({
      list: item.list || [],
      rowCount: item.rowCount || 0,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectDispatchIssueToLabour);

  // Initialize data state with list from Redux
  const [data, setData] = useState<any[]>(list || []);

  // Debug log (can be removed in production)
  console.log("Dispatch Issue To Labour list", list);

  // Use custom hook to fetch and access permissions
  const { canView } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.dispatch
  );

//   const data = list || [];
//   const data = dummyData || [];

  // Fetch IssueToLabour list
  useEffect(() => {
    const warehouseId = warehouseIdSearch || "";
    
    // Only fetch if warehouse_id is available
    if (!warehouseId) {
      console.warn("Warehouse ID is missing, skipping fetch");
      return;
    }

    dispatch(
      fetchDispatchIssueToLabourList({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseId,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  // Update data when list changes from Redux store
  useEffect(() => {
    if (list && Array.isArray(list)) {
      setData(list);
    } else {
      setData([]);
    }
  }, [list]);



  // Create menu items for DynamicMenuItem
    const menuItems: MenuItemConfig[] = useMemo(() => {
      const items: MenuItemConfig[] = [
        {
          icon: VisibilityIcon,
          label: "Details",
          permission: canView,
          onClick: (issueToLabour: any) => {
            const id = issueToLabour?.dispatch_id || issueToLabour?.id;
            if (id) {
              navigate(`/inventory-dispatch/issuelabour/view-details/${id}`);
            } else {
              toast.error("ID not found for this record");
            }
          },
          color: "info",
        },
      ];
      return items;
    }, [navigate, canView]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    if (!canView) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
            <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems, canView]);

const columns = useMemo(
  () => [
    {
      header: "Dispatch ID",
      accessorKey: "dispatch_number",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/dispatch_number/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => row.original.dispatch_number || "-",
    },
    // {
    //   header: "Labour Issue ID",
    //   accessorKey: "labour_issued_id",
    //   enableColumnFilter: true,
    //   enableSorting: true,
    //   filterType: "dropdown" as const,
    //   filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/labour_issued_id/?warehouse_id=${warehouseIdSearch}`,
    //   cell: ({ row }: { row: any }) => row.original.labour_issued_id || "-",
    // },
    {
      header: "Dispatch By",
      accessorKey: "created_by_user_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_by_user_name/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => row.original.created_by_user_name || "-",
    },
    {
      header: "Challan No.",
      accessorKey: "challan_no",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/challan_no/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => row.original.challan_no || "-",
    },
  {
    header: "Dispatch Date",
    accessorKey: "dispatch_date",
    enableColumnFilter: true,
    enableSorting: true,
    filterType: "date" as const,
    cell: ({ row }: { row: any }) => {
      const date = row.original.dispatch_date
        ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
        : "-";
      return date;
    },
  },
    // {
    //   header: "Issued Date",
    //   accessorKey: "dispatch_date",
    //   enableColumnFilter: true,
    //   enableSorting: true,
    //   filterType: "date" as const,
    //   // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/dispatch_date/?warehouse_id=${warehouseIdSearch}`,
    //   cell: ({ row }: { row: any }) => {
    //     const date = row.original.dispatch_date
    //       ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
    //       : "-";
    //     return date;
    //   },
    // },
    {
      header: "Issued From",
      accessorKey: "issued_from_warehouse_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/issued_from_warehouse_name/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => row.original.issued_from_warehouse_name || "-",
    },
    {
      header: "Labour Name",
      accessorKey: "labour_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/labour_name/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => row.original.labour_name || "-",
    },
    {
      header: "Dispatch Status",
      accessorKey: "dispatch_status",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/dispatch_status/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => (
        <StatusChip status={row.original.dispatch_status || "-"}>
          {row.original.dispatch_status
            ? row.original.dispatch_status.charAt(0).toUpperCase() +
              row.original.dispatch_status.slice(1)
            : "-"}
        </StatusChip>
      ),
    },
    {
      header: "Remarks",
      accessorKey: "remarks",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => (
        <TruncatedTextCell
          text={row?.original?.remarks}
          maxWidth="300px"
          maxLines={4}
          rowIndex={row.index}
          tooltipId={`remarks-tooltip-${row.index}`}
        />
      ),
    },
    {
      header: "Created By / Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by_user_name || "-";
        const date = row.original.created_at
          ? new Date(row.original.created_at).toISOString().split("T")[0]
          : "-";
        return `${name} - ${date}`;
      },
    },
    {
      header: "Updated By / Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by_user_name || "-";
        const date = row.original.updated_at
          ? new Date(row.original.updated_at).toISOString().split("T")[0]
          : "-";
        return `${name} - ${date}`;
      },
    },

    ...DynamicAction,
  ],
  [menuItems, DynamicAction, warehouseIdSearch]
);


  return (
    <>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row>
        <Col lg={12}>
          <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={Array.isArray(data) ? data : []}
                totalRecords={rowCount || 0}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default Dispatch;
